/*
 * The floating layer — how a menu, a listbox or a hover card escapes the box it was written inside.
 *
 * ── The bug this exists to end ──────────────────────────────────────────────────────────────────
 *
 * Every overlay in this package was an absolutely-positioned child of its trigger, which works
 * exactly until the trigger sits inside something that clips. Two things in this product clip, and
 * both do it for good reasons:
 *
 *   - `.tf-panel` sets `overflow: hidden`, which is what lets a band run edge to edge and still be
 *     cut to the panel's corner radius. A hover card in a panel header is cut off mid-word.
 *   - `.tf-table-scroll` sets `overflow-x: auto` so a wide table scrolls instead of pushing the
 *     page sideways. **CSS computes the other axis to `auto` the moment one axis is not `visible`**
 *     — there is no such thing as one scrolling axis and one overflowing one. So a row menu opening
 *     near the last row grew a vertical scrollbar instead of floating over the rows below.
 *
 * Raising `z-index` fixes neither. `z-index` orders siblings within a stacking context; it does not
 * escape an ancestor's `overflow`. Nothing painted inside a clipped box can leave it.
 *
 * ── What replaces it ────────────────────────────────────────────────────────────────────────────
 *
 * The **top layer**, via the `popover` attribute. A popover is painted outside the document's
 * stacking and clipping entirely — no ancestor's `overflow`, `transform`, `filter` or `z-index`
 * reaches it — and it needs no portal, so the element stays where it was written in the JSX and
 * React's tree is unchanged. What it does not bring is a position: a top-layer element is out of
 * flow, so somebody has to place it against its trigger. That is what this file does.
 *
 * **Not CSS anchor positioning**, which is the elegant version of exactly this and would delete
 * `placeFloating` below. Firefox does not ship it, so the fallback would have to exist anyway — and
 * a fallback that runs for a third of users is not a fallback, it is the implementation with a
 * faster path bolted beside it.
 *
 * ── Two mechanisms, and why both ────────────────────────────────────────────────────────────────
 *
 * `position: fixed` is what does the escaping, and it is enough on its own: a fixed element's
 * containing block is the viewport, so an ancestor's `overflow` does not clip it. The `popover`
 * attribute goes on top of that for the stacking half — the top layer paints above every
 * `z-index`, so a menu can never lose to a sticky header somebody raised later.
 *
 * Keeping both is also what makes this degrade honestly. Where `popover` is unavailable — an older
 * browser, or jsdom in a test — the element is still fixed and still unclipped, and only the
 * stacking guarantee is lost. Nothing breaks silently.
 */

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

/** A rectangle, in the shape `getBoundingClientRect()` returns and a test can write by hand. */
export interface Rect {
  readonly top: number;
  readonly left: number;
  readonly width: number;
  readonly height: number;
}

export interface Viewport {
  readonly width: number;
  readonly height: number;
}

export interface Placement {
  /** Viewport coordinates, for `position: fixed`. */
  readonly top: number;
  readonly left: number;
  /** Which side of the trigger it landed on, so the caller can point an arrow or animate from it. */
  readonly side: 'block-end' | 'block-start';
  /**
   * The height it may use before it must scroll itself.
   *
   * A menu taller than the viewport is the case nobody designs for and everybody eventually hits —
   * an owner list with forty names, a row menu at 80% zoom. Without this it renders past the bottom
   * of the screen with no way to reach the last item.
   */
  readonly maxBlockSize: number;
}

/** Breathing room at the viewport edge, so a menu never sits flush against it. */
const EDGE = 8;

/** The gap between the trigger and the thing it opened. */
const GAP = 4;

/**
 * Where a floating element goes, given its trigger and the room available.
 *
 * Pure, and separated from the DOM on purpose: placement is the part that can be wrong in ways
 * nobody sees until a specific screen at a specific scroll position. Every rule below is a bug
 * somebody would otherwise report as "the menu went off the screen".
 *
 * Preference is below and start-aligned, because that is where a reader's eye already is. It flips
 * above only when below genuinely does not fit *and* above fits better — a menu that flips when it
 * would have fitted is a menu that appears to jump.
 */
export function placeFloating(
  trigger: Rect,
  floating: { readonly width: number; readonly height: number },
  viewport: Viewport,
): Placement {
  const below = viewport.height - (trigger.top + trigger.height) - GAP - EDGE;
  const above = trigger.top - GAP - EDGE;

  // Below unless it does not fit and above has more room. The second half of that matters: on a
  // short viewport neither side fits, and flipping to the smaller one helps nobody.
  const side = floating.height <= below || below >= above ? 'block-end' : 'block-start';

  const maxBlockSize = Math.max(side === 'block-end' ? below : above, 0);
  const height = Math.min(floating.height, maxBlockSize);

  const top =
    side === 'block-end'
      ? trigger.top + trigger.height + GAP
      : // Above: the *bottom* edge sits above the trigger, so the top depends on the height it
        // actually got — which may be less than it asked for.
        trigger.top - GAP - height;

  // Start-aligned, then pulled back inside the viewport if that would overflow the end edge. The
  // clamp is second so a floating element wider than the viewport lands at the start edge rather
  // than at a negative offset.
  const left = Math.max(EDGE, Math.min(trigger.left, viewport.width - floating.width - EDGE));

  return { top, left, side, maxBlockSize };
}

/* ── The hook ─────────────────────────────────────────────────────────────────────────────────── */

export interface FloatingStyle {
  readonly position: 'fixed';
  readonly insetBlockStart: string;
  readonly insetInlineStart: string;
  readonly maxBlockSize: string;
}

/**
 * Anchors a floating element to a trigger, for as long as it is open.
 *
 * The caller owns `open` — this hook does not decide when a menu opens, only where it goes once it
 * has. That separation is deliberate: opening is different for every control here (a click for a
 * row menu, a hover or focus for a person, typing for a combobox) and none of it belongs in the
 * placement.
 *
 * **It repositions on scroll and resize while open, and not otherwise.** A fixed element does not
 * move with the page, so a menu opened over a table and then scrolled would otherwise hang in the
 * air over unrelated rows. The listeners are attached only while open and removed on close, so a
 * screen of two hundred rows carries no scroll handler until somebody opens something.
 *
 * `capture: true` on the scroll listener is the part that is easy to get wrong: scroll events do
 * not bubble from a scrolling `div`, so a listener on `window` without capture hears the page
 * scrolling and stays deaf to the very container the clipping bug came from.
 */
export function useFloating<T extends HTMLElement, F extends HTMLElement>(open: boolean) {
  const triggerRef = useRef<T>(null);
  const floatingRef = useRef<F>(null);
  const [style, setStyle] = useState<FloatingStyle | undefined>(undefined);

  const reposition = useCallback(() => {
    const trigger = triggerRef.current;
    const floating = floatingRef.current;
    if (!trigger || !floating) return;

    const at = placeFloating(
      trigger.getBoundingClientRect(),
      // `offsetWidth`/`offsetHeight` rather than the rect: the rect is the *current* box, which on
      // an element already carrying a `max-block-size` from a previous placement would measure the
      // constraint rather than the content. That ratchets a menu smaller every time it reopens.
      { width: floating.offsetWidth, height: floating.scrollHeight },
      { width: window.innerWidth, height: window.innerHeight },
    );

    setStyle({
      position: 'fixed',
      insetBlockStart: `${at.top}px`,
      insetInlineStart: `${at.left}px`,
      maxBlockSize: `${at.maxBlockSize}px`,
    });
  }, []);

  // Layout effect, not effect: this runs before paint, so the element is never painted at the
  // top-start corner of the viewport for one frame before jumping to its trigger.
  useLayoutEffect(() => {
    if (!open) {
      setStyle(undefined);
      return undefined;
    }

    const floating = floatingRef.current;
    // The top layer, where available. `manual` rather than `auto`: `auto` brings light-dismiss and
    // a focus policy that fight the ones each of these controls already implements — and closing on
    // an outside click while React still believes the menu is open desynchronises the two.
    if (floating && typeof floating.showPopover === 'function' && !floating.matches(':popover-open')) {
      floating.showPopover();
    }

    reposition();

    window.addEventListener('scroll', reposition, { capture: true, passive: true });
    window.addEventListener('resize', reposition, { passive: true });
    return () => {
      window.removeEventListener('scroll', reposition, { capture: true });
      window.removeEventListener('resize', reposition);
      if (floating && typeof floating.hidePopover === 'function' && floating.matches(':popover-open')) {
        floating.hidePopover();
      }
    };
  }, [open, reposition]);

  // The floating element can change size while open — a combobox filtering as somebody types is the
  // ordinary case, and it is what turns a correctly-placed list into one that runs off the bottom.
  useEffect(() => {
    const floating = floatingRef.current;
    if (!open || !floating || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(reposition);
    observer.observe(floating);
    return () => observer.disconnect();
  }, [open, reposition]);

  return { triggerRef, floatingRef, style };
}
